import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BorderedColumn, GreyBox, PageColumn, WaitFor } from '../../components/blocks';
import { useTranslation } from '../../components/blocks/localization';
import Button from '../../components/blocks/localization/Button';
import { FilterTags } from '../../components/composites';
import {
    DateRangeFilter,
    PoIdFilter,
    SearchableSelectFilter,
    UnitsStatusFilter,
} from '../../components/composites/PurchaseOrderFilters';
import { PaginatedTable } from '../../components/constructed';
import { Download } from '../../components/icons';
import { SALES_ORDER_DEMANDS_MAX_RESULTS, SALES_ORDER_DEMANDS_PER_PAGE } from '../../constants';
import { useExportSalesOrderDemands } from '../../hooks/useExportSalesOrderDemands';
import { getResellerDetails, getResellerItems } from '../../redux/actions/resellerActionCreators';
import {
    buildFilters,
    getSalesOrderDemandList,
    salesOrderDemandChangePage,
    salesOrderDemandFilterChange,
    salesOrderDemandLoadMore,
    salesOrderDemandSortChange,
} from '../../redux/actions/salesOrderDemandActionCreators';
import { RootState, SalesOrderDemand, SalesOrderDemandSortDirection } from '../../redux/types';
import { findResellerItem } from '../../utils/resellerItemUtils';
import { useSiteMapRouter } from '../../utils/SiteMapRouter';
import { toFamilyTranslationKey } from '../../utils/textUtils';
import { createTableData, SalesOrderDemandRow } from './formatTableData';
import { usePurchaseOrderFilters } from './usePurchaseOrderFilters';

const PurchaseOrderListPage: FC<PurchaseOrderListPageProps> = ({
    resellerItems,
    resellerDetails,
    getResellerItems,
    getResellerDetails,
    resellerId,
    listState,
    getSalesOrderDemandList,
    salesOrderDemandChangePage,
    salesOrderDemandLoadMore,
    salesOrderDemandSortChange,
    salesOrderDemandFilterChange,
    locale,
}) => {
    const { t } = useTranslation(['purchaseOrderListPage', 'common']);
    const { getPath } = useSiteMapRouter();
    const { isExporting, handleExport } = useExportSalesOrderDemands();
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const resellerItemsData = resellerItems.value || [];
    const salesOrderDemands = listState.salesOrderDemandData.value || [];

    const combinedLoadState = {
        ...listState.salesOrderDemandData,
        status:
            listState.salesOrderDemandData.status === 'Loaded' &&
            (resellerDetails.status === 'Uninitialized' || resellerDetails.status === 'Loading')
                ? ('Loading' as const)
                : listState.salesOrderDemandData.status,
    };

    const { filters, options, filterTags, submitQuery, clearFilters } = usePurchaseOrderFilters({
        initialState: listState,
        resellerItemsData,
        resellerLocations: resellerDetails.value?.resellerLocations?.locations,
        onFilterChange: salesOrderDemandFilterChange,
    });

    useEffect(() => {
        if (resellerId && resellerItems.status === 'Uninitialized') {
            getResellerItems(resellerId);
        }
    }, [resellerId, resellerItems.status, getResellerItems]);

    useEffect(() => {
        if (resellerId && resellerDetails.status === 'Uninitialized') {
            getResellerDetails(resellerId);
        }
    }, [resellerId, resellerDetails.status, getResellerDetails]);

    useEffect(() => {
        if (resellerId && listState.salesOrderDemandData.status === 'Uninitialized') {
            getSalesOrderDemandList(resellerId, listState.startAt, SALES_ORDER_DEMANDS_MAX_RESULTS);
        }
    }, [resellerId, listState.salesOrderDemandData.status, listState.startAt, getSalesOrderDemandList]);

    const getItemInfo = useCallback((asin?: string) => findResellerItem(resellerItemsData, asin), [resellerItemsData]);

    const toggleExpanded = useCallback((id: string) => {
        setExpandedRows((prev) => new Set(prev.has(id) ? [...prev].filter((x) => x !== id) : [...prev, id]));
    }, []);

    const toggleExpandAll = useCallback(() => {
        const allIds = new Set(salesOrderDemands.map((sod) => sod.demandId!));
        const allExpanded = [...allIds].every((id) => expandedRows.has(id));
        setExpandedRows(allExpanded ? new Set() : allIds);
    }, [salesOrderDemands, expandedRows]);

    const onSort = useCallback(
        ({ sortColumn, sortDirection }: { sortColumn: string; sortDirection: string }) => {
            submitQuery();
            salesOrderDemandSortChange(
                sortColumn,
                SalesOrderDemandSortDirection[sortDirection as keyof typeof SalesOrderDemandSortDirection]
            );
        },
        [salesOrderDemandSortChange, submitQuery]
    );

    const onPageChange = useCallback(
        (value: string) => {
            const page = parseInt(value);
            if (!page) return;

            const newStart = SALES_ORDER_DEMANDS_PER_PAGE * (page - 1);
            if (newStart < listState.startAt + SALES_ORDER_DEMANDS_MAX_RESULTS && newStart >= listState.startAt) {
                salesOrderDemandChangePage(page, newStart);
            } else {
                salesOrderDemandLoadMore(page, newStart);
            }
        },
        [listState.startAt, salesOrderDemandChangePage, salesOrderDemandLoadMore]
    );

    const handleExportCallback = useCallback(() => {
        if (!resellerId) return;
        const timezone = resellerDetails.value?.resellerIanaTimezone || undefined;
        handleExport(
            resellerId,
            buildFilters(listState, resellerItems, timezone),
            `purchase-order-list`,
            locale,
            listState.totalRecords,
            timezone
        );
    }, [resellerId, listState, resellerItems, resellerDetails, locale, handleExport]);

    const calculateNumberOfPages = (total: number) => Math.ceil(total / SALES_ORDER_DEMANDS_PER_PAGE);

    const formatData = (demands: SalesOrderDemand[]) => {
        const startPos = (listState.currentPage - 1) * SALES_ORDER_DEMANDS_PER_PAGE - listState.startAt;
        const endPos = listState.currentPage * SALES_ORDER_DEMANDS_PER_PAGE - listState.startAt;
        const pageData = demands.slice(startPos, endPos);
        return createTableData(pageData, expandedRows, {
            expandedRows,
            toggleExpanded,
            toggleExpandAll,
            getItemInfo,
            getPath,
            totalDemands: salesOrderDemands.length,
            dateFilterField: filters.dateFilterField.value,
            t,
            timezone: resellerDetails.value?.resellerIanaTimezone || undefined,
        });
    };

    return (
        <PageColumn width={'100%'}>
            <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
                <GreyBox type={'fill'} width={'100%'} spacingInset={'small'}>
                    <Row alignmentHorizontal={'justify'} alignmentVertical={'center'}>
                        <Heading level={5} alignment={'left'}>
                            {t('purchaseOrders-pageTitle')}
                            {` (${listState.totalRecords})`}
                        </Heading>
                        <WaitFor lazyValue={combinedLoadState} showLoadingLabel={false}>
                            {(_) => (
                                <Button
                                    data-testid={'purchaseOrderListPage.exportButton'}
                                    onClick={handleExportCallback}
                                    isBusy={isExporting}
                                    disabled={listState.totalRecords === 0}
                                >
                                    <Download />
                                    <p>{t('common:export-label')}</p>
                                </Button>
                            )}
                        </WaitFor>
                    </Row>
                </GreyBox>
                <WaitFor lazyValue={combinedLoadState}>
                    {(data) => (
                        <PaginatedTable<SalesOrderDemandRow>
                            layout={'stacked'}
                            data={formatData(data)}
                            dataTestId={'purchaseOrdersTable'}
                            currentPage={listState.currentPage}
                            numberOfPages={calculateNumberOfPages(listState.totalRecords)}
                            onPageChange={onPageChange}
                            onSort={onSort}
                            sortColumn={listState.sortColumn}
                            sortDirection={listState.sortDirection}
                            rowKey={(_, rowObject) => `${rowObject.demandId}-${rowObject.item.asin}`}
                            searchButton={
                                <Button
                                    type={'primary'}
                                    onClick={submitQuery}
                                    size={'large'}
                                    data-testid={'purchaseOrderListPage.searchButton'}
                                >
                                    <p>{t('common:search-text')}</p>
                                </Button>
                            }
                            clearButton={
                                <Button
                                    type={'tertiary'}
                                    onClick={clearFilters}
                                    size={'large'}
                                    data-testid={'purchaseOrderListPage.clearButton'}
                                >
                                    <p>{t('common:clear-text')}</p>
                                </Button>
                            }
                            filterTags={<FilterTags tags={filterTags} t={t} />}
                        >
                            <PoIdFilter value={filters.poIds.value} onChange={filters.poIds.onChange} t={t} />
                            <UnitsStatusFilter
                                value={filters.unitsStatus.value}
                                onChange={filters.unitsStatus.onChange}
                                t={t}
                            />
                            <SearchableSelectFilter
                                value={filters.asin.value}
                                onChange={filters.asin.onChange}
                                options={options.asinOptions}
                                label={t('asin-filterLabel')}
                                placeholder={t('searchAsin-placeholder')}
                                dataTestId={'asinFilter'}
                            />
                            <SearchableSelectFilter
                                value={filters.resellerSku.value}
                                onChange={filters.resellerSku.onChange}
                                options={options.resellerSkuOptions}
                                label={t('resellerSku-filterLabel')}
                                placeholder={t('searchSku-placeholder')}
                                dataTestId={'resellerSkuFilter'}
                            />
                            <SearchableSelectFilter
                                value={filters.family.value}
                                onChange={filters.family.onChange}
                                options={options.familyOptions}
                                label={t('family-filterLabel')}
                                placeholder={t('searchFamily-placeholder')}
                                dataTestId={'familyFilter'}
                                formatOptionLabel={(f) => t(toFamilyTranslationKey(f), { defaultValue: f })}
                            />
                            <SearchableSelectFilter
                                value={filters.shipToLocation.value}
                                onChange={filters.shipToLocation.onChange}
                                options={options.shipToLocationOptions}
                                label={t('shipToLocation-filterLabel')}
                                placeholder={t('searchLocation-placeholder')}
                                dataTestId={'shipToLocationFilter'}
                            />
                            <DateRangeFilter
                                value={filters.dateRange.value}
                                onChange={filters.dateRange.onChange}
                                dateField={filters.dateFilterField.value}
                                onDateFieldChange={filters.dateFilterField.onChange}
                                t={t}
                                timezone={resellerDetails.value?.resellerIanaTimezone || undefined}
                            />
                        </PaginatedTable>
                    )}
                </WaitFor>
            </BorderedColumn>
        </PageColumn>
    );
};

const mapStateToProps = ({ resellerReducer, salesOrderDemandReducer, userReducer }: RootState) => ({
    resellerItems: resellerReducer.resellerItems,
    resellerDetails: resellerReducer.resellerDetails,
    resellerId: resellerReducer.selectedReseller.value?.resellerId,
    listState: salesOrderDemandReducer,
    locale: userReducer.user?.locale,
});

const mapDispatchToProps = {
    getResellerItems,
    getResellerDetails,
    getSalesOrderDemandList,
    salesOrderDemandChangePage,
    salesOrderDemandLoadMore,
    salesOrderDemandSortChange,
    salesOrderDemandFilterChange,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PurchaseOrderListPageProps = ConnectedProps<typeof connector>;
export default connector(PurchaseOrderListPage);
